import { StationStatus, StationStatusState } from "@soft-db/monitoring-api-client";
import { DateTime, Duration } from "luxon";
import { defineStore } from "pinia";
import type { Tagged } from "type-fest";
import { v4 as uuidv4 } from "uuid";
import { useCachedSites } from "../api/site-query";
import { useCachedStations } from "../api/station-query";
import { useWebsocketStore } from "./websocket";

type UnsubscribeFunction = () => void;

/**
 * StationStatusState with recently-offline and none state added.
 */
export type StationStatusStateExtended = StationStatusState | "recently-offline" | "none";

export function subscribeToLiveStatus(
	type: MaybeRefOrGetter<StatusSourceType>,
	id: MaybeRefOrGetter<string>
) {
	const store = useLiveStatusStore();

	watch(
		[() => toValue(type), () => toValue(id)],
		([type, id], _, oncleanup) => {
			if (id != null) {
				oncleanup(store.subscribe(type, id));
			}
		},
		{ immediate: true }
	);

	return computed(() => store.liveStatus(toValue(type), toValue(id)));
}

export type StatusSourceType = "station" | "site";

type StatusKey = Tagged<`${StatusSourceType}-${string}`, "status-key">;
type StatusKeyInfo = {
	type: StatusSourceType;
	id: string;
};

function extractStatusKeyInfo(key: StatusKey): StatusKeyInfo {
	const [type, id] = key.split("-");
	return {
		type: type as StatusSourceType,
		id,
	};
}

type WithTimeInfo<T> = {
	when: Date;
	data: T;
};

type StateCollection = Map<StatusKey, WithTimeInfo<StationStatusStateExtended>>;
type LiveStatusCollection = Map<StatusKey, WithTimeInfo<StationStatus>>;

/**
 * Utility to generate a StatusKey
 */
export function generateStatusKey(sourceType: StatusSourceType, sourceId: string): StatusKey {
	return `${sourceType}-${sourceId}` as StatusKey;
}

export const useLiveStatusStore = defineStore("live-status", () => {
	const { socket } = useWebsocketStore();

	// Key is the station key and value the number of subscribers
	const subscribers = shallowReactive(new Map<StatusKey, Set<string>>());

	// Updated from websocket
	const latestStatusStates = reactive<StateCollection>(new Map());

	// Updated from websocket
	const latestLiveStatuses = reactive<LiveStatusCollection>(new Map());

	function cleanCache(cache: Map<string, WithTimeInfo<unknown>>) {
		const now = new Date();
		const timeout = 10 * 60 * 1_000; // 10 minutes
		for (const [key, { when }] of cache.entries()) {
			if (now.valueOf() - when.valueOf() > timeout) {
				cache.delete(key);
			}
		}
	}
	// Clean the cache every 10 minutes for data older than 10 minutes.
	// This is to prevent memory to grow indefinitely.
	useIntervalFn(
		() => {
			cleanCache(latestStatusStates);
			cleanCache(latestLiveStatuses);
		},
		10 * 60 * 1_000 /** 10 Minutes */
	);

	const stationsCache = useCachedStations();
	const sitesCache = useCachedSites();

	function getLatestBetween<T>(
		a: WithTimeInfo<T> | null,
		b: WithTimeInfo<T> | null
	): WithTimeInfo<T> | null {
		if (a == null) {
			return b;
		}

		if (b == null) {
			return a;
		}

		if (a.when > b.when) {
			return a;
		} else {
			return b;
		}
	}

	/**
	 * Get the status from the query cache
	 */
	function cachedStatus(
		type: StatusSourceType,
		id: string
	): WithTimeInfo<StationStatus | null> | null {
		if (type === "station") {
			const station = stationsCache.value.get(id);
			if (station == null) {
				return null;
			}

			return {
				when: station.when,
				data: station.data.status,
			};
		}

		if (type === "site") {
			const site = sitesCache.value.get(id);
			if (site == null) {
				return null;
			}

			return {
				when: site.when,
				data: site.data.station?.status || null,
			};
		}

		return null;
	}

	function isRecentlyOffline(status: StationStatus, duration = Duration.fromObject({ days: 1 })) {
		const now = DateTime.now();
		const stationLastStatus = DateTime.fromISO(status.time);

		return status.state === "offline" && now.diff(stationLastStatus) < duration;
	}

	function extractStatusStateExtended(status: StationStatus | null): StationStatusStateExtended {
		if (status == null) {
			return "none";
		}

		if (isRecentlyOffline(status)) {
			return "recently-offline";
		}

		return status.state;
	}

	/**
	 * Get the status state from the query cache
	 */
	function cachedStatusState(
		type: StatusSourceType,
		id: string
	): WithTimeInfo<StationStatusStateExtended> | null {
		const cached = cachedStatus(type, id);
		if (cached == null) {
			return null;
		}

		return {
			when: cached.when,
			data: extractStatusStateExtended(cached.data),
		};
	}

	function statusState(type: StatusSourceType, id: string): StationStatusStateExtended {
		const key = generateStatusKey(type, id);

		const latest = latestStatusStates.get(key) || null;
		const cached = cachedStatusState(type, id);

		// In case the websocket connection drop and we miss an event,
		// we take the latest between the one in cache and the one we got from the socket.
		return getLatestBetween(latest, cached)?.data || "none";
	}

	/**
	 * Compose status that should be grouped together for searching and grouping.
	 * As of now, only "recently-offline" is grouped with "offline"
	 */
	function searchableStatusState(type: StatusSourceType, id: string): StationStatusStateExtended[] {
		const status = statusState(type, id);

		switch (status) {
			case "recently-offline":
				return ["recently-offline", "offline"];

			case "waiting":
				return ["waiting", "online"];

			default:
				return [status];
		}
	}

	function liveStatus(type: StatusSourceType, id: string): StationStatus | null {
		const key = generateStatusKey(type, id);

		const latest = latestLiveStatuses.get(key) || null;
		const cached = cachedStatus(type, id);

		// In case the websocket connection drop and we miss an event,
		// we take the latest between the one in cache and the one we got from the socket.
		return getLatestBetween(latest, cached)?.data || null;
	}

	/**
	 * Register on the server to get notified each time the live status is updated
	 * @returns Unsubscribe function
	 */
	function subscribe(type: StatusSourceType, id: string): UnsubscribeFunction {
		const uuid = uuidv4();

		const key = generateStatusKey(type, id);

		let subs = subscribers.get(key);
		if (subs == null) {
			subs = new Set();
			subscribers.set(key, subs);

			// new sub, init listener
			void listen(key);
		}

		subs.add(uuid);

		return () => {
			subs.delete(uuid);
			if (subs.size === 0) {
				subscribers.delete(key);
				void unlisten(key);
			}
		};
	}

	async function listen(key: StatusKey) {
		if (!socket.connected) {
			return;
		}

		const { type, id } = extractStatusKeyInfo(key);

		await socket.request("LiveStatus.Listen", { type, id });
	}

	async function unlisten(key: StatusKey) {
		if (!socket.connected) {
			return;
		}

		const { type, id } = extractStatusKeyInfo(key);

		await socket.request("LiveStatus.Unlisten", { type, id });
	}

	let registered = false;
	async function register() {
		if (registered) {
			return;
		}

		if (!socket.connected) {
			return;
		}

		registered = true;
		await socket.request("StatusState.Register", null);

		for (const key of subscribers.keys()) {
			await listen(key);
		}
	}
	void register();

	socket.on("open", register);
	socket.on("close", async () => {
		registered = false;
	});

	// --- Register handlers on socket ---

	type StatusUpdatePayload = {
		station: string;
		site: string | null;
		state: StationStatusState;
	};
	socket.on("StatusState.Update", ({ station, site, state }: StatusUpdatePayload) => {
		let stateExtended: StationStatusStateExtended = state;

		// When we receive the offline state, it's always in the 24 hours for the recently-offline window.
		// We force it here, for simplicity in the rest of the app.
		if (stateExtended === "offline") {
			stateExtended = "recently-offline";
		}

		latestStatusStates.set(generateStatusKey("station", station), {
			data: stateExtended,
			when: new Date(),
		});
		if (site != null) {
			latestStatusStates.set(generateStatusKey("site", site), {
				data: stateExtended,
				when: new Date(),
			});
		}
	});

	type LiveStatusUpdatePayload = {
		type: StatusSourceType;
		id: string;
		status: StationStatus;
	};
	socket.on("LiveStatus.Update", ({ type, id, status }: LiveStatusUpdatePayload) => {
		const key = generateStatusKey(type, id);
		latestLiveStatuses.set(key, { data: status, when: new Date() });
	});

	return {
		latestLiveStatuses,
		latestStatusStates,
		liveStatus,
		statusState,
		searchableStatusState,
		subscribe,
	};
});
